import React, { useEffect, useRef, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';

interface SectionCardProps {
  title: string;
  subtitle?: string;
  count?: number;
  collapseKey?: number;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

export function SectionCard({
  title,
  subtitle,
  count,
  collapseKey,
  defaultExpanded = false,
  children,
}: SectionCardProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const previousCollapseKey = useRef(collapseKey);
  const borderSoft = alpha(theme.palette.divider, 0.12);
  const accent = theme.palette.primary.main;
  const label = count !== undefined ? `${title} (${count})` : title;

  useEffect(() => {
    if (collapseKey === previousCollapseKey.current) {
      return;
    }

    previousCollapseKey.current = collapseKey;
    setExpanded(false);
  }, [collapseKey]);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: 2,
        border: `1px solid ${borderSoft}`,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded((value) => !value);
          }
        }}
        sx={{
          px: 1.75,
          py: 1,
          borderBottom: expanded ? `1px solid ${borderSoft}` : 'none',
          bgcolor: alpha(accent, 0.06),
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.primary">
            {label}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary" display="block">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <ExpandMoreIcon
          fontSize="small"
          color="action"
          sx={{
            flexShrink: 0,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
          }}
        />
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 1.75, py: 1.25 }}>
          <Stack spacing={2}>{children}</Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}
